// Cálculos derivados de los reportes de Recepción que se usan en DOS lugares: los componentes de
// la pantalla y la exportación (PDF / Excel). Viven acá como funciones puras para que el documento
// exportado no pueda decir algo distinto de lo que se ve en pantalla.
import { COLOR } from './tokens';

export const CLAVE_SIN_CLASIFICAR = 'SIN_CLASIFICAR';

// La persona ya era cliente y no abrió ningún trámite en esta visita: vino a traer un papel, a un
// turno médico, a tomar vista, a volver de ANSES. No es una consulta y no compite con los otros
// desenlaces, así que el backend la separa y la deja SIN porcentaje — la pantalla la muestra
// aparte, con su cantidad. Son 77 de 217 atenciones en septiembre: sacarlas del denominador es lo
// que hace que "Inicia ahora" deje de calcularse sobre gente que nunca iba a iniciar nada.
export const CLAVE_TRAMITE_EN_CURSO = 'TRAMITE_EN_CURSO';

const NOMBRES_MES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export const etiquetaMes = (mes, anio) => `${NOMBRES_MES[mes - 1] || mes} ${anio}`;

// ── Resultado de la atención ─────────────────────────────────────────────────

// Un color = un significado para los resultados sembrados (V224 y V255): verde el mejor desenlace
// (se abrió trámite), azul uno pendiente (vuelve con documentación), ámbar el caso viable que se
// perdió (no quiso dejar carpeta), rojo el caso que no prospera, gris el dato que no se cargó
// (oscuro si al menos se sabe que ya tenía carpeta).
const COLOR_RESULTADO = {
    'Inicia ahora': COLOR.verde,
    'Carpeta a futuro': COLOR.azul,
    'No quiso dejar carpeta': COLOR.ambar,
    'No viable': COLOR.rojo,
    [CLAVE_TRAMITE_EN_CURSO]: COLOR.grisTexto,
    [CLAVE_SIN_CLASIFICAR]: COLOR.grisFrio,
};

// Los resultados que el estudio agregue desde Datos Maestros no tienen semántica asignada. Se les
// dan tonos claros, uno distinto por resultado: con uno solo para todos, dos resultados nuevos
// apilados en la misma barra quedarían indistinguibles.
// Sin ámbar: es el de "No quiso dejar carpeta".
const COLORES_RESULTADOS_NUEVOS = [COLOR.azulClaro, COLOR.verdeClaro, COLOR.rojoClaro, COLOR.negro];

/** Mapa clave de resultado → color, estable para el orden de claves recibido. */
export function asignarColoresResultado(claves) {
    const mapa = new Map();
    let indiceNuevo = 0;
    (claves || []).forEach((clave) => {
        if (mapa.has(clave)) return;
        mapa.set(clave, COLOR_RESULTADO[clave]
            || COLORES_RESULTADOS_NUEVOS[indiceNuevo++ % COLORES_RESULTADOS_NUEVOS.length]);
    });
    return mapa;
}

/** Posición de cierre: 0 los desenlaces, 1 "Vino por un trámite en curso", 2 "Sin clasificar". */
const rangoCierre = (clave) => {
    if (clave === CLAVE_SIN_CLASIFICAR) return 2;
    if (clave === CLAVE_TRAMITE_EN_CURSO) return 1;
    return 0;
};

/**
 * Los dos últimos van siempre al final y por motivos distintos: "Vino por un trámite en curso" no
 * es un desenlace de consulta (está fuera del porcentaje) y "Sin clasificar" es la ausencia del
 * dato. El resto conserva el orden recibido (sort estable).
 */
function compararSinClasificarAlFinal(a, b) {
    return rangoCierre(a) - rangoCierre(b);
}

/**
 * Filas de /resultado-atencion listas para graficar: el orden del backend (cantidad desc) con
 * "Sin clasificar" al final, y el color de cada una.
 */
export function filasResultadoAtencion(data) {
    const filas = [...(data || [])].sort((a, b) => compararSinClasificarAlFinal(a.resultado, b.resultado));
    const colores = asignarColoresResultado(filas.map((f) => f.resultado));
    return filas.map((f) => ({ ...f, color: colores.get(f.resultado) }));
}

/**
 * Series de /tendencia-por-tipo. El backend manda, por mes, la lista de resultados que tuvieron
 * turnos ese mes (catálogo editable, V224): acá se arma una serie por resultado presente en el
 * período, con 0 en los meses donde no apareció.
 * @param {Array<{anio:number, mes:number, total:number, resultados:Array<{resultado:string, resultadoLabel:string, cantidad:number}>}>} data
 * @returns {{meses: Array, series: Array<{clave:string, etiqueta:string, color:string, valores:number[], total:number}>}}
 */
export function seriesTendenciaPorTipo(data) {
    const meses = (data || []).map((m) => ({ ...m, label: etiquetaMes(m.mes, m.anio) }));
    const totales = new Map();
    const etiquetas = new Map();
    meses.forEach((m) => (m.resultados || []).forEach((r) => {
        totales.set(r.resultado, (totales.get(r.resultado) || 0) + (r.cantidad || 0));
        etiquetas.set(r.resultado, r.resultadoLabel || r.resultado);
    }));

    const claves = [...totales.keys()].sort((a, b) =>
        compararSinClasificarAlFinal(a, b) || totales.get(b) - totales.get(a));
    const colores = asignarColoresResultado(claves);

    const series = claves.map((clave) => ({
        clave,
        etiqueta: etiquetas.get(clave),
        color: colores.get(clave),
        total: totales.get(clave),
        valores: meses.map((m) => (m.resultados || []).find((r) => r.resultado === clave)?.cantidad || 0),
    }));
    return { meses, series };
}

// ── Ocupación ────────────────────────────────────────────────────────────────

// Bloques de 2 horas — el nombre del bloque es el horario de inicio par ("08:00"→"08:00–10:00").
export function bloqueDe(horario) {
    const hora = Number(String(horario).split(':')[0]);
    const inicio = hora - (hora % 2);
    const fin = inicio + 2;
    return `${String(inicio).padStart(2, '0')}:00–${String(fin).padStart(2, '0')}:00`;
}

/**
 * Días del período en que hubo turnos (los que trae /por-dia). La capacidad configurada es POR FRANJA
 * Y POR DÍA, mientras que /por-horario y /heatmap-ocupacion suman todos los días del período: sin
 * dividir por esto, un mes entero se comparaba contra la capacidad de un solo día.
 */
export const diasConTurnos = (porDia) => Math.max(1, (porDia || []).length);

/** Promedio de turnos por franja y por día: la unidad en la que está expresada la capacidad. */
export const promedioPorDia = (total, dias) => (total || 0) / Math.max(1, dias || 1);

/**
 * Franjas donde se atendió por encima de la capacidad. No se deducen de los totales: el sistema las
 * marca solo, cada turno que excede el cupo es un sobreturno.
 */
export const franjasConSobreturnos = (porHorario) => (porHorario || []).filter((f) => (f.sobreturnos || 0) > 0);

/** Ocupación media (%): turnos por franja y por día contra la capacidad de una franja en un día. */
export function ocupacionMediaFranjas(porHorario, capacidad, dias) {
    const filas = porHorario || [];
    if (!filas.length || !capacidad) return null;
    const total = filas.reduce((acc, f) => acc + (f.total || 0), 0);
    return (promedioPorDia(total / filas.length, dias) / capacidad) * 100;
}

/**
 * Agrega las franjas de 15 min en bloques de 2h, desde el mismo array que alimenta el gráfico de
 * demanda. La ocupación es turnos por día / (capacidad × franjas con datos del bloque): el total del
 * período se divide por los días con turnos para quedar en la misma unidad que la capacidad.
 */
export function calcularBloquesHorarios(porHorario, tiempoEspera, capacidad, dias = 1) {
    const diasPeriodo = Math.max(1, dias || 1);
    const esperaPorHorario = new Map((tiempoEspera || []).map((e) => [e.horario, {
        media: Number(e.tiempoEsperaPromedioMinutos), n: e.turnosMedidos || 0,
    }]));
    const mapa = new Map();
    (porHorario || []).forEach((f) => {
        const clave = bloqueDe(f.horario);
        if (!mapa.has(clave)) {
            mapa.set(clave, { bloque: clave, turnos: 0, bot: 0, atendidos: 0, sobreturnos: 0, esperaSum: 0, esperaN: 0, franjas: 0 });
        }
        const b = mapa.get(clave);
        b.turnos += f.total || 0;
        b.bot += f.bot || 0;
        b.atendidos += f.atendidos || 0;
        b.sobreturnos += f.sobreturnos || 0;
        b.franjas += 1;
        const espera = esperaPorHorario.get(f.horario);
        if (espera && !Number.isNaN(espera.media)) {
            // Ponderada por los turnos medidos de cada franja: así el bloque sale de los mismos turnos
            // que el KPI del encabezado y no del promedio de los promedios. Sin ese dato (los ejemplos
            // de la ayuda) cada franja pesa lo mismo.
            const peso = espera.n > 0 ? espera.n : 1;
            b.esperaSum += espera.media * peso;
            b.esperaN += peso;
        }
    });
    return Array.from(mapa.values())
        .sort((a, b) => a.bloque.localeCompare(b.bloque))
        .map((b) => ({
            ...b,
            espera: b.esperaN ? b.esperaSum / b.esperaN : null,
            ocupacion: capacidad && b.franjas ? (b.turnos / diasPeriodo / (capacidad * b.franjas)) * 100 : 0,
        }));
}

// ── Resultados ───────────────────────────────────────────────────────────────

/**
 * Los tres pasos del embudo del turno (Turnos del período → Se presentaron → Trámite abierto) con la
 * caída absoluta y porcentual respecto del anterior.
 *
 * No hace falta un paso intermedio: los turnos que no son atención al público (cobros, turnos médicos,
 * ANSES...) y quienes vinieron por un trámite que ya tenían en curso quedan fuera de TODAS las
 * estadísticas desde el backend (28-sep-2026), así que ya no están en "Turnos del período". Con los días
 * cerrados "Se presentaron" son las atenciones finalizadas, y la caída del último paso es 100 − conversión.
 */
export function calcularPasosEmbudo({ agendado, presentaron, conTramite }) {
    const valores = [
        { titulo: 'Turnos del período', valor: agendado || 0 },
        { titulo: 'Se presentaron', valor: presentaron || 0 },
        { titulo: 'Trámite abierto', valor: conTramite || 0 },
    ];
    return valores.map((p, i) => {
        const anterior = i > 0 ? valores[i - 1].valor : null;
        const hayBase = anterior != null && anterior > 0;
        const perdida = hayBase ? anterior - p.valor : null;
        const caidaPct = hayBase ? (perdida / anterior) * 100 : null;
        return { ...p, perdida, caidaPct };
    });
}

/**
 * Conversión del equipo ponderada: trámites abiertos sobre los turnos con que se calculó cada
 * conversión (baseConversion), NO el promedio simple de los % de cada operador. Es el mismo criterio de
 * la conversión global del encabezado (selectors.conversionGlobal): un operador con 5 turnos no pesa
 * igual que uno con 100. Sin la base (respuestas viejas) cae al promedio simple.
 */
export function conversionEquipo(operadores) {
    const filas = operadores || [];
    const base = filas.reduce((acc, f) => acc + (f.baseConversion || 0), 0);
    if (base > 0) return (filas.reduce((acc, f) => acc + (f.conTramite || 0), 0) / base) * 100;
    const tasas = filas.map((f) => Number(f.tasaConversion)).filter((v) => !Number.isNaN(v));
    return tasas.length ? tasas.reduce((a, b) => a + b, 0) / tasas.length : null;
}
